import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Helmet } from 'react-helmet';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const SandboxCalculator = () => {
  const [expression, setExpression] = useState('');
  const [result, setResult] = useState('');
  const [error, setError] = useState('');
  const [showDetailedAnalysis, setShowDetailedAnalysis] = useState(false);
  const [calculationHistory, setCalculationHistory] = useState([]);

  const handleInputChange = (e) => {
    setExpression(e.target.value);
    setError('');
  };

  const handleCheckboxChange = (e) => {
    setShowDetailedAnalysis(e.target.checked);
  };

  const calculateResult = () => {
    if (!expression) {
      setError('Please enter an expression');
      return;
    }

    try {
      const safeExpression = expression.replace(/[^0-9+\-*/().]/g, '');
      const startTime = performance.now();
      const result = eval(safeExpression); // eslint-disable-line no-eval
      const endTime = performance.now();
      
      const calculationData = {
        expression,
        result,
        time: (endTime - startTime).toFixed(2) + 'ms'
      };
      
      setCalculationHistory(prev => [calculationData, ...prev.slice(0, 4)]);
      setResult(result);
      setError('');
    } catch (error) {
      setError('Invalid expression');
      setResult('');
    }
  };

  const generateChartData = () => {
    return {
      labels: ['Result'],
      datasets: [
        {
          label: 'Calculation Result',
          data: [result],
          backgroundColor: 'rgba(75, 192, 192, 0.5)',
          borderColor: 'rgba(75, 192, 192, 1)',
          borderWidth: 1
        }
      ]
    };
  };

  return (
    <div className="min-h-screen bg-gray-100 py-8">
      <Helmet>
        <title>Sandbox Calculator | Advanced Math Experiments</title>
        <meta name="description" content="An advanced sandbox calculator for experimenting with complex mathematical expressions. Get instant visual feedback and detailed analysis of your calculations." />
        <meta name="keywords" content="advanced calculator, math experiments, real-time calculations, mathematical analysis" />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
          <div className="flex items-center mb-8">
            <div className="text-4xl text-blue-600 mr-4">🧮</div>
            <h1 className="text-3xl font-bold text-gray-800">Sandbox Calculator</h1>
          </div>

          <div className="text-gray-600 mb-8">
            <p>Experiment with complex mathematical expressions and get instant visual feedback.</p>
            <ul className="list-disc pl-6 mt-2">
              <li>✓ Real-time calculation results</li>
              <li>✓ Visual representation of results</li>
              <li>✓ Calculation history tracking</li>
              <li>✓ Detailed performance metrics</li>
            </ul>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-white rounded-lg shadow-lg p-6"
          >
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-1">Enter Expression:</label>
              <input
                type="text"
                value={expression}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., (2 + 3) * 4 / 2"
              />
            </div>

            <div className="flex items-center mb-4">
              <input
                type="checkbox"
                id="showDetailedAnalysis"
                checked={showDetailedAnalysis}
                onChange={handleCheckboxChange}
                className="form-checkbox h-5 w-5 text-blue-600"
              />
              <label className="ml-2 text-sm font-medium text-gray-700">
                Show detailed analysis and visualization
              </label>
            </div>

            <button
              onClick={calculateResult}
              className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors duration-200"
            >
              Calculate
            </button>

            {error && (
              <div className="mt-4 p-4 text-red-700 bg-red-100 rounded-lg">
                {error}
              </div>
            )}

            {result && (
              <div className="mt-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-6">Calculation Results</h2>
                
                {!showDetailedAnalysis ? (
                  <div className="mt-4 p-4 text-green-700 bg-green-100 rounded-lg">
                    Result: {result}
                  </div>
                ) : (
                  <div>
                    <div className="bg-white p-6 rounded-lg shadow-md mb-8">
                      <Bar
                        data={generateChartData()}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          scales: {
                            y: {
                              beginAtZero: true,
                              title: {
                                display: true,
                                text: 'Result Value'
                              }
                            },
                            x: {
                              title: {
                                display: true,
                                text: 'Calculation'
                              }
                            }
                          },
                          plugins: {
                            title: {
                              display: true,
                              text: 'Calculation Result Visualization',
                              font: {
                                size: 16
                              }
                            }
                          }
                        }}
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                      <div className="bg-gray-50 p-6 rounded-lg">
                        <h3 className="text-lg font-semibold text-gray-700 mb-2">Calculation Details</h3>
                        <p className="text-gray-600">
                          Expression: {expression}<br />
                          Result: {result}<br />
                          Calculation Time: {calculationHistory[0]?.time}
                        </p>
                      </div>

                      <div className="bg-gray-50 p-6 rounded-lg">
                        <h3 className="text-lg font-semibold text-gray-700 mb-2">Recent Calculations</h3>
                        <ul className="space-y-2">
                          {calculationHistory.slice(0, 5).map((calc, index) => (
                            <li key={index} className="flex items-center">
                              <span className="font-medium text-gray-600 mr-2">{index + 1}.</span>
                              {calc.expression} = {calc.result}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </motion.div>

          <div className="mt-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">How to Use Sandbox Calculator <span className="text-blue-600">📋</span></h2>
            <div className="text-gray-600">
              <p>Follow these steps to use the calculator:</p>
              
              <div className="mt-4">
                <h3 className="text-lg font-semibold text-blue-600 mb-2">Step 1: Enter an Expression</h3>
                <ul className="list-disc pl-6 mt-2">
                  <li>Type a mathematical expression using numbers and operators</li>
                  <li>Use parentheses for order of operations</li>
                </ul>
              </div>

              <div className="mt-6">
                <h3 className="text-lg font-semibold text-blue-600 mb-2">Step 2: Calculate</h3>
                <ul className="list-disc pl-6 mt-2">
                  <li>Click "Calculate" to see the result</li>
                </ul>
              </div>

              <div className="mt-6">
                <h3 className="text-lg font-semibold text-blue-600 mb-2">Step 3: Analyze Results</h3>
                <ul className="list-disc pl-6 mt-2">
                  <li>View the result in numerical form</li>
                  <li>Enable detailed analysis for visual representation</li>
                  <li>Check calculation history for previous results</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="mt-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">FAQ <span className="text-blue-600">❓</span></h2>
            <div className="text-gray-600 space-y-4">
              <div>
                <h3 className="font-semibold">What types of expressions can I use?</h3>
                <p>You can use basic arithmetic operations (+, -, *, /) with numbers and parentheses for complex expressions.</p>
              </div>
              <div>
                <h3 className="font-semibold">Is this calculator safe to use?</h3>
                <p>Yes, all expressions are sanitized before evaluation to prevent security risks.</p>
              </div>
              <div>
                <h3 className="font-semibold">Why can't I use advanced functions?</h3>
                <p>This sandbox calculator focuses on basic arithmetic operations for simplicity and safety.</p>
              </div>
            </div>
          </div>

          <div className="mt-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Example Calculation <span className="text-blue-600">🔢</span></h2>
            <div className="text-gray-600 bg-gray-50 p-6 rounded-lg">
              <p><strong>Input Expression:</strong></p>
              <code className="bg-gray-100 p-2 rounded">(2 + 3) * 4 / 2</code>
              <p className="mt-4"><strong>Result:</strong></p>
              <p className="text-green-700 font-medium">10</p>
              <p className="mt-2 text-sm text-gray-500">Calculation: (5) * 4 / 2 = 20 / 2 = 10</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SandboxCalculator;